import { ActionParameterValueTypeEnum, type IMetaAction, type IPostMetaAction } from "../types/scad";

export default function useMetaAction(
  Action: IMetaAction,
  DefaultRequestData: Indexable = {}
) {
  const Url = ref<string>("");
  const Response = ref<Indexable>({});
  const ResponseData = ref<any>(null);
  const RequestData = ref<Indexable>(DefaultRequestData);
  const isError = ref<boolean>(false);
  const app = useAppStore();

  const toast = useToast();

  function extractResponseData(response: Indexable, key: string) {
    if (key.includes("&")) {
      const data = key
        .split("&")[0]
        .slice(0, -1)
        .replace(/\]$/, "")
        .replace("[", ".")
        .replace("]", ".")
        .split(".")
        .reduce((a, b) => a[b], response);

      let dataString = "";

      if (data) {
        (data as any[]).forEach((element, index) => {
          dataString +=
            "- " +
            key
              .split("&")[1]
              .slice(1)
              .replace(/\]$/, "")
              .replace("[", ".")
              .replace("]", ".")
              .split(".")
              .reduce((a, b) => a[b], element);
          if (index != (data as any[]).length - 1) {
            dataString += "\n";
          }
        });
      }

      return dataString;
    }

    if (key.includes("*")) {
      const data = key
        .split("*")[0]
        .slice(0, -1)
        .replace(/\]$/, "")
        .replace("[", ".")
        .replace("]", ".")
        .split(".")
        .reduce((a, b) => a[b], response);

      const dataArray: any[] = [];

      if (data) {
        (data as any[]).forEach((element, index) => {
          dataArray.push(
            "- " +
              key
                .split("*")[1]
                .slice(1)
                .replace(/\]$/, "")
                .replace("[", ".")
                .replace("]", ".")
                .split(".")
                .reduce((a, b) => a[b], element)
          );
        });
      }

      return dataArray;
    }
    return key
      .replace(/\]$/, "")
      .replace("[", ".")
      .replace("]", ".")
      .split(".")
      .reduce((a, b) => a[b], response);
  }

  function processEndpoint(
    endpoint: string,
    tokenData: { [key: string]: any }
  ): string {
    const tokenPattern = /\{(\w+)\}/g; //pattern to find field tokens eg {id}, {branchId}, etc

    let replacedString: string = endpoint.replace(
      tokenPattern,
      (match, token) => {
        const replacement = tokenData[token];

        return replacement !== undefined ? replacement : 0;
      }
    );
    return replacedString;
  }

  /**
   * Generates the request configuration for the meta action
   */
  function generateRequestConfig(context: Indexable, isBulk: boolean = false) {
    setUrlValue();
    const url = getUrlValue(isBulk, context);

    const isFileDownload =
      Action.PostActionResponse.toLowerCase() === "filedownload";

    let requestData = {};

    switch (Action.FilterQueryType?.toUpperCase()) {
      case "SERIALIZED":
        requestData = {
          RecordContext: context,
        };
        break;
      case "BOTH":
        requestData = {
          ...context,
          RecordContext: JSON.stringify(context),
        };
        break;
      case "APPENDQUERIES":
        if ("queryFilters" in context) {
          const queryFilters = context["queryFilters"];

          requestData = {
            ...queryFilters,
          };
        }
        break;
      default:
        requestData = { ...RequestData.value, ...context };
    }

    const isGetMethod = ["get"].includes(Action.Method?.toLowerCase() ?? "get");

    const options = {
      method: Action.Method,
      headers: {
        ContentType: "application/json",
      },
      [isGetMethod ? "query" : "body"]: {
        ...requestData,
        ...Action.Body,
      },
      ...(isFileDownload && { responseType: "blob" }),
    } as any;

    return {
      url: url,
      options,
      requestData,
    };
  }

  /**
   * Handles the response and formats it according to the action type
   */
  async function handleResponse(): Promise<IPostMetaAction> {
    // Extract specific response data if a key is provided
    if (Action.PostActionResponseKey && Response.value) {
      try {
        ResponseData.value = extractResponseData(
          Response.value,
          Action.PostActionResponseKey
        );
      } catch (e: any) {
        // Silently fail extraction, keep original ResponseData
      }
    }

    // Handle different response types
    switch (Action.PostActionResponse.toLowerCase()) {
      case "toast":
        const message = Action.ResponseMessage ?? "Action Completed";
        if (isError.value) {
          // Handle error toasts
          if (ResponseData.value instanceof Array) {
            ResponseData.value.forEach((data) => {
              toast.add({
                severity: "error",
                summary: `${Action.Name} Failed`,
                detail: data,
                life: 5000,
              });
            });
          } else {
            toast.add({
              severity: "error",
              summary: `${Action.Name} Failed`,
              detail:
                (Action.PostActionResponseKey
                  ? ResponseData.value ?? "Unknown error"
                  : ResponseData.value?.ResponseStatus?.Message) ??
                "Unknown error",
              life: 5000,
            });
          }
        } else {
          // Handle success toast
          toast.add({
            severity: "success",
            summary: "Success!",
            detail: processSlugs(message, ResponseData.value ?? {}),
            life: 5000,
          });
        }
        return {
          action: "toast",
          message: processSlugs(message, ResponseData.value ?? {}),
        };

      case "filedownload":
        return {
          action: "filedownload",
          data: await Response.value._data,
          headers: Response.value.headers,
        };

      case "dialog":
        return {
          action: "dialog",
          data: ResponseData.value ?? null,
        };

      default:
        return {
          action: Action.PostActionResponse,
          data: await Response.value._data,
        };
    }
  }

  /**
   * Executes the meta action with the provided context
   */
  async function execute(context: Indexable): Promise<IPostMetaAction> {
    isError.value = false;

    try {
      // Generate request configuration
      const { url, options } = generateRequestConfig(context);

      try {
        if (Action.PostActionResponse.toLowerCase() === "navigate") {
          // Append request data to url as query parameters
          const finalUrl = new URL(url, window.location.origin);
          if ("queryFilters" in context) {
            const queryFilters = context["queryFilters"];
            Object.keys(queryFilters).forEach((key) => {
              finalUrl.searchParams.append(key, queryFilters[key]);
            });
          }
          if (options.method?.toLowerCase() === "get") {
            Object.keys(options.query || {}).forEach((key) => {
              const value = options.query[key];
              finalUrl.searchParams.append(
                key,
                typeof value === "object"
                  ? JSON.stringify(value)
                  : String(value)
              );
            });
          }

          return {
            action: "navigate",
            data: null,
            url: finalUrl.toString(),
          };
        }
        // Execute the request in raw mode
        const rawResponse = await $fetch.raw(url, options);

        // Store the full response object with headers and metadata
        Response.value = rawResponse;
        ResponseData.value = rawResponse._data ?? null;
      } catch (e: any) {
        // Handle fetch errors - raw mode errors also have response object
        Response.value = e?.response ?? e;
        ResponseData.value = e?.response?._data ?? null;
        isError.value = true;
      }

      // Handle and format the response
      return await handleResponse();
    } catch (err) {
      return {
        action: "error",
        error: err,
      };
    }
  }

  /**
   * Generates bulk request configuration from multiple contexts
   */
  function generateBulkRequestData(
    contexts: Indexable[],
    userInput: Indexable = {}
  ): Indexable {
    let processedContext: Indexable = {};
    Action.ActionParameters?.forEach((param) => {
      if (param.ActionParameterType != "Request") return;
      if (param.ActionParameterValueType == "UserInput") {
        if (userInput[param.Key] !== undefined) {
          processedContext[param.Key] = userInput[param.Key];
        }
      } else if (
        param.ActionParameterValueType ==
        ActionParameterValueTypeEnum.BulkFormParameter
      ) {
        processedContext[param.Key] = contexts.map(
          (ctx) => ctx[param.ValueKeyName!]
        );
      }
    });
    return processedContext;
  }

  /**
   * Executes the meta action for multiple contexts in a single bulk request
   */
  async function executeBulk(
    contexts: Indexable[],
    userInput: Indexable = {},
    additionalRequestData: Indexable = {}
  ): Promise<boolean> {
    isError.value = false;

    try {
      // Generate bulk request configuration
      RequestData.value = generateBulkRequestData(contexts, userInput);
      const url = getUrlValue(true);

      // Execute the bulk request
      try {
        const response = await $fetch.raw(url, {
          method: Action.Method,
          body: {
            ...RequestData.value,
            ...additionalRequestData,
          },
        });

        Response.value = response;
        ResponseData.value = response._data ?? null;
      } catch (e: any) {
        Response.value = e?.response ?? e;
        ResponseData.value = e?.response?._data ?? null;
        isError.value = true;

        return false;
      }
    } catch (err) {
      return false;
    }

    return isError.value == false;
  }

  const hasConfirmationDialog = computed(() => {
    // if confirm is false, then no need to check dialog details
    if (Action.UseConfirmationDialog == false) {
      return false;
    }

    // confirm must be true and dialog details must be set
    if (
      Action.UseConfirmationDialog == true &&
      (Action?.ConfirmationHeaderText?.length ?? 0) > 0 &&
      (Action?.ConfirmationBodyText?.length ?? 0) > 0
    ) {
      return true;
    }

    return false;
  });

  function getUrlValue(
    isBulk: boolean = false,
    additionalContext: Indexable = {}
  ) {
    if (isBulk) {
      // When doing a bulk request, urls are handled on the server side and don't need to have the proxy prefix
      return processEndpoint("/scad" + Action.Route, {
        ...RequestData.value,
        ...additionalContext,
      });
    }
    if (Action.ExecutionLocation == "Client") {
      return processEndpoint("/scad" + Action.Route, {
        ...RequestData.value,
        ...additionalContext,
      });
    } else {
      return `/scad/meta/actions/${Action.Id}/execute`;
    }
  }

  function setUrlValue() {
    if (Action.ExecutionLocation == "Client") {
      Url.value = processEndpoint("/scad" + Action.Route, RequestData.value);
    } else {
      Url.value = `/scad/meta/actions/${Action.Id}/execute`;
    }
  }

  // set url on load:
  setUrlValue();

  return {
    Url,
    Response,
    RequestData,
    ResponseData,
    execute,
    Execute: execute,
    executeBulk,
    isError,
    hasConfirmationDialog,
  };
}
